import {
	Vue, Component, Ref, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import { BModal } from 'bootstrap-vue';
import Swal from 'sweetalert2';
import mitt from 'mitt';
import { TabEvent } from 'interfaces/app';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import { createInstance } from 'utils/vue';
import CustomSelect from 'components/CustomSelect';
import CurrencyService from 'services/CurrencyService';
import JsonCurrency from '../../data/iso-currency.json';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.CurrencyModel>>();
@Component({
	components: {
		TabulatorBtn,
		CustomSelect,
	},
	emits: ['routeCurrency'],
	mixins: [Template],
})
class CurrencyTable extends Vue {
	@Ref('addcurrency-modal') readonly modal!: BModal;

	@Ref('currencyTable')
	private readonly tableReference!: HTMLDivElement;

	private currency : DB.CurrencyModel[] = [];

	private currencyData = {
		id: '',
		data: {} as DB.CurrencyModel,
	};

	private jsonCurrency = JsonCurrency;

	private isLoaded = false;

	private table?: Tabulator;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'id',
				title: 'ID',
				headerFilter: true,
			},
			{
				title: 'Name',
				field: 'name',
				headerFilter: true,
			},
			{
				title: 'Sign',
				field: 'sign',
				headerFilter: true,
			},
			{
				title: 'Prefix',
				field: 'prefix',
				headerFilter: true,
			},
			{
				title: 'Suffix',
				field: 'suffix',
				headerFilter: true,
			},
			{
				title: 'Separator',
				field: 'separator',
				headerFilter: true,
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.BadgeModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteCurrency,
		);
		eventBus.on(
			'editItem',
			this.editCurrency,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deleteCurrency,
		);
		eventBus.off(
			'editItem',
			this.editCurrency,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const data = await CurrencyService.getAll(parameter);
			this.table?.setData(data);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected addCurrency(): void {
		this.isLoaded = true;
		CurrencyService.create(
			{
				iso: this.currencyData.data.iso,
				name: this.currencyData.data.name,
				sign: this.currencyData.data.sign,
				prefix: '',
				suffix: '',
				separator: undefined,
				default: 0,
			},
		).then((res) => {
			this.modal.hide();

			this.$emit(
				'routeCurrency',
				res.id,
			);
			return undefined;
		})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$toastError(err.message);
			});
	}

	private editCurrency(data: TabEvent<DB.CurrencyModel>['editItem']): void {
		this.$emit(
			'routeCurrency',
			data.id,
		);
	}

	private deleteCurrency(data: TabEvent<DB.CurrencyModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading...');
				// eslint-disable-next-line promise/no-nesting
				CurrencyService.delete(data.id).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(CurrencyTable);
